import { SelectComponent } from '@/components/select';
import { Button } from '@/components/ui/button';
import { FormField } from '@/components/ui/form';
import { resetCells, resetDistricts, resetSectors, resetVillages } from '@/store/slices';
import { AppDispatch, RootState } from '@/store/store';
import {
  fetchCellsBySector,
  fetchDistrictsByProvince,
  fetchProvinces,
  fetchSectorsByDistrict,
  fetchVillagesByCell
} from '@/store/thunks';
import { MapPinned, Save, X } from 'lucide-react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { TransferFormValues } from '../schema';

interface TransferFormProps {
  onSubmitVillage(village: string): void;
  onCancel(e: boolean): void;
}

const Asterisk = () => <span className="text-red-500">*</span>;

export const TransferForm = ({ onSubmitVillage, onCancel }: TransferFormProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const form = useFormContext<TransferFormValues>();
  const { watch, setValue } = form;

  const provinceId = watch('province');
  const districtId = watch('district');
  const sectorId = watch('sector');
  const cellId = watch('cell');
  const villageId = watch('village');

  const { items: provinces } = useSelector((state: RootState) => state.provinces);
  const { districtsByProvince } = useSelector((state: RootState) => state.districts);
  const { sectorsByDistrict } = useSelector((state: RootState) => state.sectors);
  const { cellsBySector } = useSelector((state: RootState) => state.cells);
  const { villagesByCell } = useSelector((state: RootState) => state.villages);

  useEffect(() => {
    dispatch(fetchProvinces({ page: 1, limit: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (provinceId) {
      dispatch(fetchDistrictsByProvince(provinceId));
    } else {
      dispatch(resetDistricts());
      setValue('district', '');
    }
  }, [provinceId, dispatch, setValue]);

  useEffect(() => {
    if (districtId) {
      dispatch(fetchSectorsByDistrict(districtId));
    } else {
      dispatch(resetSectors());
      setValue('sector', '');
    }
  }, [districtId, dispatch, setValue]);

  useEffect(() => {
    if (sectorId) {
      dispatch(fetchCellsBySector(sectorId));
    } else {
      dispatch(resetCells());
      setValue('cell', '');
    }
  }, [sectorId, dispatch, setValue]);

  useEffect(() => {
    if (cellId) {
      dispatch(fetchVillagesByCell(cellId));
    } else {
      dispatch(resetVillages());
      setValue('village', '');
    }
  }, [cellId, dispatch, setValue]);

  const handleProvinceChange = (value: string) => {
    setValue('province', value);
    setValue('district', '');
    setValue('sector', '');
    setValue('cell', '');
    setValue('village', '');
  };

  const handleDistrictChange = (value: string) => {
    setValue('district', value);
    setValue('sector', '');
    setValue('cell', '');
    setValue('village', '');
  };

  const handleSectorChange = (value: string) => {
    setValue('sector', value);
    setValue('cell', '');
    setValue('village', '');
  };

  const handleCellChange = (value: string) => {
    setValue('cell', value);
    setValue('village', '');
  };

  const handleVillageChange = (value: string) => {
    setValue('village', value);
  };

  const onSubmit = (data: TransferFormValues) => {
    onSubmitVillage(data.village);
  };

  const handleCancel = () => onCancel(true);

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
      <div className="flex items-center gap-3 rounded-md border border-[#dce8f7] bg-[#f3f8ff] p-4">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-white text-[#2374cf] shadow-sm">
          <MapPinned className="h-5 w-5" />
        </span>
        <div>
          <h5 className="font-semibold text-[#172641]">{t('TextUpdateVotingLocation')}</h5>
          <p className="mt-0.5 text-xs leading-5 text-[#71809a]">
            {t('TextVoterTransferDescription')}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-x-5 gap-y-5 md:grid-cols-2">
        <FormField
          control={form.control}
          name="province"
          render={({ fieldState }) => (
            <SelectComponent
              label={
                <span className="inline-flex items-center gap-0.5">
                  {t('TextProvince')}
                  <Asterisk />
                </span>
              }
              options={provinces.map((province) => ({
                label: province.name,
                value: province.id
              }))}
              onValueChange={handleProvinceChange}
              value={provinceId}
              placeholder={t('PSelectProvince')}
              errorMessage={fieldState.error?.message}
            />
          )}
        />

        {provinceId && (
          <FormField
            control={form.control}
            name="district"
            render={({ fieldState }) => (
              <SelectComponent
                label={
                  <span className="inline-flex items-center gap-0.5">
                    {t('TextDistrict')}
                    <Asterisk />
                  </span>
                }
                options={districtsByProvince?.map((district) => ({
                  label: district.name,
                  value: district.id
                }))}
                onValueChange={handleDistrictChange}
                value={districtId}
                placeholder={t('PSelectDistrict')}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}

        {districtId && (
          <FormField
            control={form.control}
            name="sector"
            render={({ fieldState }) => (
              <SelectComponent
                label={
                  <span className="inline-flex items-center gap-0.5">
                    {t('TextSector')}
                    <Asterisk />
                  </span>
                }
                options={sectorsByDistrict?.map((sector) => ({
                  label: sector.name,
                  value: sector.id
                }))}
                onValueChange={handleSectorChange}
                value={sectorId}
                placeholder={t('PSelectSector')}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}

        {sectorId && (
          <FormField
            control={form.control}
            name="cell"
            render={({ fieldState }) => (
              <SelectComponent
                label={
                  <span className="inline-flex items-center gap-0.5">
                    {t('TextCell')}
                    <Asterisk />
                  </span>
                }
                options={cellsBySector?.map((cell) => ({
                  label: cell.name,
                  value: cell.id
                }))}
                onValueChange={handleCellChange}
                value={cellId}
                placeholder={t('PSelectCell')}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}

        {cellId && (
          <FormField
            control={form.control}
            name="village"
            render={({ fieldState }) => (
              <SelectComponent
                label={
                  <span className="inline-flex items-center gap-0.5">
                    {t('TextVillage')}
                    <Asterisk />
                  </span>
                }
                options={villagesByCell?.map((village) => ({
                  label: village.name,
                  value: village.id
                }))}
                onValueChange={handleVillageChange}
                value={villageId}
                placeholder={t('PSelectVillage')}
                errorMessage={fieldState.error?.message}
              />
            )}
          />
        )}
      </div>

      <div className="flex flex-col-reverse gap-3 border-t border-[#edf1f6] pt-5 sm:flex-row sm:justify-end">
        <Button
          variant="outline"
          type="button"
          onClick={handleCancel}
          className="h-11 rounded-md border-[#dce5f1] px-5 text-[#465572] hover:bg-[#f3f6fa]"
        >
          <X className="mr-2 h-4 w-4" />
          {t('TextCancel')}
        </Button>
        <Button className="bg-[#338AF3] hover:bg-blue-800" type="submit">
          <Save className="mr-2 h-4 w-4" />
          {t('TextSaveChanges')}
        </Button>
      </div>
    </form>
  );
};
